/**
 * HomePage — Page d'accueil (ex-DashboardPage)
 * Contenu identique à DashboardPage SANS le wrapper <Layout> interne
 * Le layout est fourni par AppShell via App.tsx
 * Titre renommé : "Tableau de Bord" → "Accueil"
 */

import { useEffect, useState } from 'react';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { Link } from 'react-router-dom';
import { patientService } from '@/shared/services/patientService';
import { apiClient } from '@/shared/services/apiClient';
import { API_ENDPOINTS } from '@/config/api';

interface HitlJob {
  id: string;
  agent_id: string;
  acte_type: string | null;
  created_at: string | null;
}

export function HomePage() {
  const { user } = useAuth();
  const [patientCount, setPatientCount] = useState<number | null>(null);
  const [hitlJobs, setHitlJobs] = useState<HitlJob[]>([]);

  useEffect(() => {
    patientService.list({ size: 1 })
      .then((r) => setPatientCount(r.total))
      .catch(() => setPatientCount(null));
    apiClient.get<{ items: HitlJob[]; count: number }>(API_ENDPOINTS.jobsHitl)
      .then((r) => setHitlJobs(r.items))
      .catch(() => setHitlJobs([]));
  }, []);

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <h1 className="fw-bold mb-2" style={{ color: 'var(--gray-900)' }}>
            <i className="bi bi-house-fill me-2" style={{ color: 'var(--purple-primary)' }}></i>
            Accueil
          </h1>
          <p className="text-muted">Bienvenue {user?.prenom}</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="row g-4 mb-4">
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card bounce-in">
            <div className="card-body text-center">
              <i className="bi bi-people-fill text-primary" style={{ fontSize: '2.5rem' }}></i>
              <h3 className="mt-3 mb-0 fw-bold">
                {patientCount !== null ? patientCount : <span className="spinner-border spinner-border-sm" />}
              </h3>
              <p className="text-muted mb-0">Patients actifs</p>
            </div>
          </div>
        </div>
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card bounce-in" style={{ animationDelay: '0.1s' }}>
            <div className="card-body text-center">
              <i className="bi bi-file-text-fill text-success" style={{ fontSize: '2.5rem' }}></i>
              <h3 className="mt-3 mb-0 fw-bold text-muted" style={{ fontSize: '1.2rem' }}>—</h3>
              <p className="text-muted mb-0">Observations</p>
            </div>
          </div>
        </div>
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card bounce-in" style={{ animationDelay: '0.2s' }}>
            <div className="card-body text-center">
              <i className="bi bi-mic-fill text-warning" style={{ fontSize: '2.5rem' }}></i>
              <h3 className="mt-3 mb-0 fw-bold text-muted" style={{ fontSize: '1.2rem' }}>—</h3>
              <p className="text-muted mb-0">Vocaux aujourd'hui</p>
            </div>
          </div>
        </div>
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card bounce-in" style={{ animationDelay: '0.3s' }}>
            <div className="card-body text-center">
              <i className="bi bi-graph-up-arrow text-info" style={{ fontSize: '2.5rem' }}></i>
              <h3 className="mt-3 mb-0 fw-bold text-muted" style={{ fontSize: '1.2rem' }}>—</h3>
              <p className="text-muted mb-0">Précision ASR</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="row g-4">
        {/* Profil */}
        <div className="col-12 col-lg-4">
          <div className="card">
            <div className="card-header bg-gradient-light">
              <i className="bi bi-person-circle me-2 text-primary"></i>
              <strong>Votre Profil</strong>
            </div>
            <div className="card-body">
              <div className="d-flex flex-column gap-3">
                <div>
                  <small className="text-muted text-uppercase fw-semibold d-block">Nom complet</small>
                  <span className="fw-semibold">{user?.prenom} {user?.nom}</span>
                </div>
                <div>
                  <small className="text-muted text-uppercase fw-semibold d-block">Email</small>
                  <span>{user?.email}</span>
                </div>
                <div>
                  <small className="text-muted text-uppercase fw-semibold d-block">Profil</small>
                  <span className="badge bg-primary">{user?.profil}</span>
                </div>
                <div>
                  <small className="text-muted text-uppercase fw-semibold d-block">Service</small>
                  <span>{user?.service || 'Non défini'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Accès rapides */}
        <div className="col-12 col-lg-8">
          <div className="card">
            <div className="card-header bg-gradient-light">
              <i className="bi bi-lightning-charge me-2 text-primary"></i>
              <strong>Accès Rapides</strong>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <Link to="/patients" className="text-decoration-none">
                    <div className="card h-100 border-2 border-primary">
                      <div className="card-body text-center">
                        <i className="bi bi-people-fill text-primary" style={{ fontSize: '2rem' }}></i>
                        <h5 className="mt-2 mb-1">Patients</h5>
                        <p className="text-muted small mb-0">CRUD, recherche, export</p>
                      </div>
                    </div>
                  </Link>
                </div>
                <div className="col-12 col-md-6">
                  <Link to="/vocal" className="text-decoration-none">
                    <div className="card h-100 border-2 border-success">
                      <div className="card-body text-center">
                        <i className="bi bi-mic-fill text-success" style={{ fontSize: '2rem' }}></i>
                        <h5 className="mt-2 mb-1">Saisie vocale</h5>
                        <p className="text-muted small mb-0">Dictée · Mains libres · Wake word</p>
                      </div>
                    </div>
                  </Link>
                </div>
                <div className="col-12 col-md-6">
                  <Link to="/admin/llm-config" className="text-decoration-none">
                    <div className="card h-100 border-2 border-danger">
                      <div className="card-body text-center">
                        <i className="bi bi-cpu text-danger" style={{ fontSize: '2rem' }}></i>
                        <h5 className="mt-2 mb-1">Config LLM</h5>
                        <p className="text-muted small mb-0">
                          <span className="badge bg-danger-subtle text-danger">Admin</span>
                        </p>
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* HITL — Jobs en attente (US-115) */}
      {hitlJobs.length > 0 && (
        <div className="row mt-4">
          <div className="col-12">
            <div className="card border-warning">
              <div className="card-header d-flex align-items-center gap-2" style={{ background: 'rgba(255,193,7,0.1)' }}>
                <i className="bi bi-hourglass-split text-warning"></i>
                <strong>Actions agentiques en attente de validation</strong>
                <span className="badge bg-warning text-dark ms-auto">{hitlJobs.length}</span>
              </div>
              <div className="card-body p-0">
                {hitlJobs.map((job) => (
                  <div key={job.id} className="d-flex align-items-center justify-content-between px-3 py-2"
                    style={{ borderBottom: '1px solid var(--gray-100)' }}>
                    <div>
                      <span className="badge bg-secondary me-2" style={{ fontSize: '0.7rem' }}>
                        {job.acte_type ?? 'inconnu'}
                      </span>
                      <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                        Agent : {job.agent_id}
                      </span>
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() =>
                          apiClient.patch(`${API_ENDPOINTS.jobsHitlApprove(job.id)}`, {})
                            .then(() => setHitlJobs((prev) => prev.filter((j) => j.id !== job.id)))
                            .catch(() => {})
                        }
                      >
                        <i className="bi bi-check me-1"></i>Approuver
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() =>
                          apiClient.patch(`${API_ENDPOINTS.jobsHitlReject(job.id)}`, {})
                            .then(() => setHitlJobs((prev) => prev.filter((j) => j.id !== job.id)))
                            .catch(() => {})
                        }
                      >
                        <i className="bi bi-x me-1"></i>Refuser
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="alert alert-success d-flex align-items-center">
            <i className="bi bi-check-circle-fill me-2"></i>
            <div>
              <strong>Système opérationnel</strong> — Horaliia MVP v0.1 · Pipeline voice actif · Mini-DPI prêt
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
